import React, { useMemo, useState } from "react";
import { sortedStories, randomFeatured, fmtDate } from "../../data/impactStories";
import { ArrowUpRight, Sparkles } from "lucide-react";

const cats = ["All", "Environment", "People", "Governance", "Community", "Safety", "Innovation"];

export default function ImpactWall({ onOpenStory }) {
  const [filter, setFilter] = useState("All");
  const featured = useMemo(() => randomFeatured(), []);
  const shown = filter === "All" ? sortedStories : sortedStories.filter((s) => s.categories.includes(filter));

  return (
    <section id="wall" data-testid="ri-impact-wall" className="py-24 lg:py-32 bg-[#0c0c0f] scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10 reveal">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">The Impact Wall</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
              Every story, <span className="text-amber-accent">on the record.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="wall-filters">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`wall-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em] font-semibold border transition-all duration-300 ${
                  filter === c ? "bg-[#F5D000] text-black border-[#F5D000]" : "text-white/55 border-white/15 hover:border-white/40 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Featured story of this visit */}
        {filter === "All" && featured && (
          <button
            data-testid="wall-featured-story"
            onClick={() => onOpenStory(featured)}
            className="ri-fade-up group w-full text-left mb-6 rounded-2xl overflow-hidden border border-[#F5D000]/25 bg-[#101014] grid grid-cols-1 md:grid-cols-2 hover:border-[#F5D000]/60 transition-all"
          >
            <div className="relative h-64 md:h-80 overflow-hidden">
              <img src={featured.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5D000] text-black text-[10px] uppercase tracking-[0.18em] font-bold">
                <Sparkles className="w-3 h-3" /> Featured this visit
              </div>
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <div className="text-xs text-white/45 mb-3">{fmtDate(featured.date)} · {featured.project}</div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">{featured.title}</h3>
              <p className="text-white/65 leading-relaxed mb-5">{featured.summary}</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#F5D000] group-hover:gap-3.5 transition-all">
                Open the story <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </button>
        )}

        {/* Masonry */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="wall-masonry">
          {shown.map((s, i) => (
            <button
              key={s.id}
              data-testid={`wall-card-${s.id}`}
              onClick={() => onOpenStory(s)}
              className="wall-card ri-fade-up group relative w-full text-left mb-5 rounded-2xl overflow-hidden border border-white/8 bg-[#101014] break-inside-avoid hover:border-[#F5D000]/50 transition-all"
              style={{ animationDelay: `${(i % 6) * 50}ms` }}
            >
              <div className={`relative overflow-hidden ${s.size === "large" ? "h-72" : s.size === "tall" ? "h-96" : "h-52"}`}>
                <img src={s.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute top-3.5 left-3.5 flex flex-wrap gap-1.5">
                  {s.categories.map((c) => (
                    <span key={c} className="px-2 py-0.5 rounded bg-black/65 backdrop-blur text-[9px] uppercase tracking-[0.16em] text-[#F5D000] font-bold">{c}</span>
                  ))}
                </div>
                {/* hover summary */}
                <div className="absolute inset-x-0 bottom-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-400">
                  <div className="text-[10px] text-white/50 mb-1.5">{fmtDate(s.date)}</div>
                  <h3 className="font-display text-lg font-bold text-white leading-snug">{s.title}</h3>
                  <p className="mt-2 text-xs text-white/70 leading-relaxed opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-24 transition-all duration-400 line-clamp-3">
                    {s.summary}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
